import { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Ship, Building2, Plus, Trash2, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AdminFleetAssignment = () => {
  const qc = useQueryClient();
  const [selectedVessel, setSelectedVessel] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [fieldName, setFieldName] = useState("");

  const { data: companies = [] } = useQuery({
    queryKey: ["admin-companies"],
    queryFn: async () => {
      const { data } = await supabase.from("companies").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: vessels = [] } = useQuery({
    queryKey: ["admin-fleet-vessels"],
    queryFn: async () => {
      const { data } = await supabase.from("fleet_vessels").select("*").order("name");
      return data ?? [];
    },
  });

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ["admin-vessel-contracts"],
    queryFn: async () => {
      const { data } = await supabase
        .from("vessel_contracts")
        .select("*, fleet_vessels(name, vessel_type), companies(name)")
        .order("created_at", { ascending: false });
      return data ?? [];
    },
  });

  const assignVessel = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("vessel_contracts").insert({
        vessel_id: selectedVessel,
        company_id: selectedCompany,
        field_name: fieldName || null,
        is_active: true,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setSelectedVessel("");
      setSelectedCompany("");
      setFieldName("");
      qc.invalidateQueries({ queryKey: ["admin-vessel-contracts"] });
      toast.success("Vessel assigned to client");
    },
    onError: () => toast.error("Failed to assign vessel"),
  });

  const removeAssignment = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("vessel_contracts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vessel-contracts"] });
      toast.success("Assignment removed");
    },
    onError: () => toast.error("Failed to remove assignment"),
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase.from("vessel_contracts").update({ is_active: !isActive }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vessel-contracts"] });
      toast.success("Assignment updated");
    },
    onError: () => toast.error("Failed to update assignment"),
  });

  return (
    <AdminLayout>
      <div className="mb-8">
        <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin</p>
        <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
          Fleet <span className="accent-text">Assignment</span>
        </h1>
        <p className="text-primary-foreground/40 font-body text-sm mt-2">
          Assign vessels to client companies. Changes are reflected across the client portal in real-time.
        </p>
      </div>

      {/* Assign Form */}
      <div className="glass-card rounded-2xl p-6 mb-6">
        <h2 className="font-display text-lg text-primary-foreground mb-4">Assign Vessel to Client</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select value={selectedVessel} onValueChange={setSelectedVessel}>
            <SelectTrigger className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground">
              <SelectValue placeholder="Select Vessel" />
            </SelectTrigger>
            <SelectContent>
              {vessels.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.name} ({v.vessel_type})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="bg-primary-foreground/5 border-primary-foreground/10 text-primary-foreground">
              <SelectValue placeholder="Select Client" />
            </SelectTrigger>
            <SelectContent>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <input
            type="text"
            value={fieldName}
            onChange={e => setFieldName(e.target.value)}
            placeholder="Field / Location (optional)"
            className="px-4 py-2 bg-primary-foreground/5 border border-primary-foreground/10 rounded-md text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-teal/40"
          />

          <button
            onClick={() => { if (selectedVessel && selectedCompany) assignVessel.mutate(); }}
            disabled={!selectedVessel || !selectedCompany || assignVessel.isPending}
            className="px-6 py-2 bg-gradient-to-r from-teal to-teal-light text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-lg hover:shadow-teal/25 transition-all font-body disabled:opacity-50"
          >
            <span className="flex items-center gap-2 justify-center"><Plus className="w-4 h-4" /> Assign</span>
          </button>
        </div>
      </div>

      {/* Current Assignments */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      ) : contracts.length === 0 ? (
        <div className="glass-card rounded-2xl p-16 text-center">
          <Ship className="w-12 h-12 text-primary-foreground/15 mx-auto mb-4" />
          <p className="text-primary-foreground/40 font-body">No vessel assignments yet. Assign vessels to clients above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {contracts.map((contract: any) => (
            <div key={contract.id} className="glass-card rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center">
                <Ship className="w-5 h-5 text-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-primary-foreground font-body">
                    {contract.fleet_vessels?.name || "Unknown Vessel"}
                  </p>
                  <ArrowRight className="w-3.5 h-3.5 text-primary-foreground/30" />
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 text-teal/60" />
                    <span className="text-sm text-primary-foreground/60 font-body">{contract.companies?.name || "Unknown"}</span>
                  </span>
                </div>
                <p className="text-[11px] text-primary-foreground/30 font-body">
                  {contract.field_name && `Field: ${contract.field_name} · `}
                  {contract.is_active ? "Active" : "Inactive"}
                  {contract.contract_start && ` · From ${contract.contract_start}`}
                </p>
              </div>

              <button
                onClick={() => toggleActive.mutate({ id: contract.id, isActive: contract.is_active })}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                  contract.is_active
                    ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"
                    : "bg-primary-foreground/5 text-primary-foreground/30 hover:bg-primary-foreground/10"
                }`}
              >
                {contract.is_active ? "Active" : "Inactive"}
              </button>

              <button
                onClick={() => { if (confirm("Remove this vessel assignment?")) removeAssignment.mutate(contract.id); }}
                className="p-2 rounded-lg text-primary-foreground/20 hover:text-destructive hover:bg-destructive/5 transition-all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminFleetAssignment;
